import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { channelLabel } from '@/components/ui/tag';
import { PlatformIcon } from '@/components/PlatformIcon';
import { OutreacherPicker } from '@/components/money/OutreacherPicker';
import { useCreateOutreachAccount, useUpdateOutreachAccount } from '@/hooks/useOutreachAccounts';
import { ACCOUNT_STATUS_LABEL } from '@/lib/outreachAccounts';
import { cn } from '@/lib/utils';
import {
  INVITE_CHANNELS,
  OUTREACH_ACCOUNT_STATUSES,
  type InviteChannel,
  type OutreachAccount,
  type OutreachAccountInput,
  type OutreachAccountStatus,
} from '@/api/types';

/** A row of toggle buttons — fewer clicks than a dropdown for 3–5 choices. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  render,
}: {
  label: string;
  value: T | '';
  options: readonly T[];
  onChange: (v: T) => void;
  render: (v: T) => ReactNode;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap gap-0.5 rounded-lg border border-input bg-[var(--input-bg)] p-0.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={value === o}
          onClick={() => onChange(o)}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition-colors pointer-coarse:h-10',
            value === o ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {render(o)}
        </button>
      ))}
    </div>
  );
}

const textareaClass =
  'rounded-lg border border-input bg-[var(--input-bg)] px-3 py-2 text-sm placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25';

/**
 * Add or edit one vault account. On the outreacher's profile the outreacher
 * is fixed for a new account; editing can always reassign. When editing, a
 * blank password field keeps the stored one.
 */
export function AccountFormDialog({
  open,
  onOpenChange,
  account,
  fixedInviter,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account being edited; absent = add. */
  account?: OutreachAccount | null;
  /** Add for this outreacher (profile page). */
  fixedInviter?: { publicId: string; name: string };
}): JSX.Element {
  const create = useCreateOutreachAccount();
  const update = useUpdateOutreachAccount();
  const editing = !!account;

  const [inviterId, setInviterId] = useState('');
  const [platform, setPlatform] = useState<InviteChannel | ''>('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [status, setStatus] = useState<OutreachAccountStatus>('ACTIVE');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time it opens.
  useEffect(() => {
    if (!open) return;
    setInviterId('');
    setPlatform(account?.platform ?? '');
    setUsername(account?.username ?? '');
    setEmail(account?.email ?? '');
    setPassword('');
    setShowPassword(false);
    setClearPassword(false);
    setStatus(account?.status ?? 'ACTIVE');
    setNotes(account?.notes ?? '');
    setError(null);
  }, [open, account]);

  const pending = create.isPending || update.isPending;
  const needsInviter = !editing && !fixedInviter;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (needsInviter && !inviterId) return setError('Pick the outreacher who uses this account.');
    if (!platform) return setError('Pick a platform.');
    if (!username.trim().replace(/^@+/, '')) return setError('Enter the username.');

    const body: OutreachAccountInput = {
      platform,
      username: username.trim(),
      email: email.trim() || null,
      notes: notes.trim() || null,
      status,
    };
    if (password) body.password = password;
    else if (editing && clearPassword) body.password = null;

    try {
      if (account) {
        if (inviterId) body.inviterId = inviterId;
        await update.mutateAsync({ publicId: account.publicId, body });
        toast.success(`Saved @${body.username}`);
      } else {
        body.inviterId = fixedInviter?.publicId ?? inviterId;
        await create.mutateAsync(body);
        toast.success(`Added @${body.username}`);
      }
      setPassword('');
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit @${account!.username}` : 'Add account'}</DialogTitle>
          <DialogDescription>
            {editing
              ? `Used by ${account!.inviter.name}. Only admins can see these logins.`
              : fixedInviter
                ? `A login ${fixedInviter.name} messages creators from. Only admins can see it.`
                : 'A login an outreacher messages creators from. Only admins can see it.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="grid gap-3" noValidate autoComplete="off">
          {(needsInviter || editing) && (
            <div className="grid gap-1">
              <Label htmlFor="oa-inviter">{editing ? 'Move to another outreacher (optional)' : 'Outreacher'}</Label>
              <OutreacherPicker
                id="oa-inviter"
                value={inviterId}
                onChange={(id) => setInviterId(id)}
                placeholder={editing ? `Keep ${account!.inviter.name}` : 'Search outreachers…'}
              />
            </div>
          )}
          <div className="grid gap-1">
            <Label>Platform</Label>
            <Segmented
              label="Platform"
              value={platform}
              options={INVITE_CHANNELS}
              onChange={setPlatform}
              render={(c) => (
                <>
                  <PlatformIcon platform={c} size={12} />
                  {channelLabel(c)}
                </>
              )}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label htmlFor="oa-username">Username</Label>
              <Input id="oa-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="@handle" autoComplete="off" />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="oa-email">Login email (optional)</Label>
              <Input id="oa-email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </div>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="oa-password">{editing ? 'New password' : 'Password'}</Label>
            <div className="flex gap-1">
              <Input
                id="oa-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (e.target.value) setClearPassword(false);
                }}
                // Never let the browser offer the admin's own saved logins here.
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                placeholder={editing && account!.hasPassword ? 'Leave blank to keep the current one' : undefined}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide what I typed' : 'Show what I typed'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {editing && account!.hasPassword && !password && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[var(--primary)]"
                  checked={clearPassword}
                  onChange={(e) => setClearPassword(e.target.checked)}
                />
                Remove the stored password
              </label>
            )}
            <p className="text-xs text-muted-foreground">Stored encrypted. Every reveal and change is logged.</p>
          </div>
          <div className="grid gap-1">
            <Label>Status</Label>
            <Segmented
              label="Status"
              value={status}
              options={OUTREACH_ACCOUNT_STATUSES}
              onChange={setStatus}
              render={(s) => ACCOUNT_STATUS_LABEL[s]}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="oa-notes">Notes (optional)</Label>
            <textarea
              id="oa-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="2FA phone, warm-up state, anything the next admin needs"
              className={textareaClass}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {editing ? 'Save' : 'Add account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { textareaClass };
